import { useState } from 'react';
import { VehicleSummaryHeader } from '../domain/VehicleSummaryHeader';
import { ChallanList } from '../domain/ChallanList';
import { StickyBottomBar } from '../domain/StickyBottomBar';
import { NudgeHeaderChip } from '../nudges/NudgeHeaderChip';
import { NudgeInlineCard } from '../nudges/NudgeInlineCard';
import { NudgeStickyBanner } from '../nudges/NudgeStickyBanner';
import { NudgeWhitespaceCard } from '../nudges/NudgeWhitespaceCard';
import { NudgePayNowAdjacency } from '../nudges/NudgePayNowAdjacency';
import { defaultVehicle } from '../../fixtures/vehicles';
import { challanFixtures } from '../../fixtures/challans';
import { fastagProfiles } from '../../fixtures/fastagProfiles';
import { computeRecommendedAmount } from '../../utils/qualification';
import type { VariantKey, QualificationState, FastagProfile } from '../../types';

interface StaticListingPreviewProps {
  variant: VariantKey;
  qualification: QualificationState;
  listLength?: 'empty' | 'short' | 'long' | 'mixed';
  balance?: number | null;
}

export function StaticListingPreview({
  variant,
  qualification,
  listLength = 'long',
  balance = null,
}: StaticListingPreviewProps) {
  const baseProfile: FastagProfile = fastagProfiles[qualification] ?? fastagProfiles.low;
  const profile: FastagProfile = {
    ...baseProfile,
    balance: balance ?? baseProfile.balance,
    recommendedAmount: computeRecommendedAmount(balance ?? baseProfile.balance),
  };

  const challans = challanFixtures[listLength];
  const [selectedIds, setSelectedIds] = useState<string[]>(
    () => challans.filter((c) => c.status === 'pending').map((c) => c.id)
  );

  const isQualified = qualification !== 'unqualified';
  const isShortList = challans.length <= 2;

  const showA = variant === 'recommended' || variant === 'A';
  const showB = variant === 'recommended' || variant === 'B';
  const showC = variant === 'recommended' || variant === 'C';
  const showD = variant === 'recommended' || variant === 'D';
  const showE = variant === 'E';

  const bQualifies = isQualified && (qualification === 'low' || qualification === 'predicted-low');
  const cQualifies = isQualified && (qualification === 'negative' || (qualification === 'predicted-low' && profile.recentDecline));
  const dQualifies = isQualified && isShortList && (qualification === 'low' || qualification === 'predicted-low');

  const selectedChallans = challans.filter((c) => selectedIds.includes(c.id));
  const total = selectedChallans.reduce((sum, c) => sum + c.amount, 0);

  return (
    <div className="flex h-full flex-col bg-slate-50 dark:bg-slate-900">
      <header className="flex h-14 shrink-0 items-center border-b border-slate-100 bg-white px-4 dark:border-slate-800 dark:bg-slate-800">
        <h1 className="text-base font-semibold text-slate-900 dark:text-slate-50">Challans</h1>
      </header>

      <VehicleSummaryHeader
        vehicle={defaultVehicle}
        chipSlot={showA && isQualified ? <NudgeHeaderChip profile={profile} visible={isQualified} /> : null}
      />

      <main className="flex-1 overflow-y-auto">
        <div className="flex flex-col gap-3 pt-2">
          {showB && (
            <NudgeInlineCard
              profile={profile}
              visible={bQualifies}
              dismissed={false}
              onRecharge={() => {}}
              onDismiss={() => {}}
            />
          )}
          {showE && <NudgePayNowAdjacency profile={profile} visible={isQualified} onRecharge={() => {}} />}
          <ChallanList
            challans={challans}
            selectedIds={selectedIds}
            onToggle={(id) => {
              setSelectedIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              );
            }}
          />
          {showD && (
            <NudgeWhitespaceCard
              profile={profile}
              visible={dQualifies}
              dismissed={false}
              onRecharge={() => {}}
              onDismiss={() => {}}
            />
          )}
        </div>
      </main>

      <div className="shrink-0">
        {showC && (
          <div className="mx-4 mb-3">
            <NudgeStickyBanner
              profile={profile}
              visible={cQualifies}
              dismissed={false}
              onRecharge={() => {}}
              onDismiss={() => {}}
            />
          </div>
        )}
        <StickyBottomBar total={total} count={selectedChallans.length} onPay={() => {}} disabled />
      </div>
    </div>
  );
}
